import React, { useState } from 'react';
import { Button, Card, Placeholder, Grid } from 'semantic-ui-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import crossIcon from '../../semantic-ui/site/assets/cross-icon.svg';
import downlinkThroughPutImg from '../../semantic-ui/site/assets/download-blk.png';
import uploadThroughPutImg from '../../semantic-ui/site/assets/upload-blk.png';
import { Range, getTrackBackground } from 'react-range';
import { prepareOverallWidgetContent, processGaugeChartData } from '../../helpers/performanceUtil';
import GaugeChart from '../common/charts/GaugeChart';
import { getInfo } from './KPIViewPage';
import DonutChart from '../common/charts/DonutChart';
import { useSelector } from 'react-redux';
import { getMainSubtitleClass, roles, userAllowed } from '../../helpers/utils';
import InformationIcon from './InformationIcon';

export const OverallWidget = (props) => {
    const {
        enterpriseId = '',
        locationId = '',
        deviceCount = 0,
        coreCapacity = 0,
        attachedDevicesCount = 0,
        loading = true,
        title = '',
        KPIValue = 0,
        thresholds = {},
        defaultThresholds = {},
        kpiType = '',
        throughputValues = [],
        onSubmit,
        tooltip,
        COLORS,
        data,
        type,
        leftValue
    } = props;
    const { roles: userRoles } = useSelector((state) => state.auth);
    const { name: dlName, image: dlImage } = getInfo('DOWNLINK_THROUGHPUT');
    const { name: ulName, image: ulImage } = getInfo('UPLINK_THROUGHPUT');
    const [DlValue, UlValue] = throughputValues;
    const [showSlider, setShowSlider] = useState(false);
    const [modifyLoading, setModifyLoading] = useState(false);
    const [level1, setLevel1] = useState(thresholds?.level1);
    const [level3, setLevel3] = useState(thresholds?.level3);
    const isKPI4GCoreDLThroughput = userAllowed(userRoles, [roles.MANAGE_4G_CORE_DL_THROUGHPUT]);
    const isKPI4GCoreULThroughput = userAllowed(userRoles, [roles.MANAGE_4G_CORE_UL_THROUGHPUT]);
    const isKPI4GRANDLThroughput = userAllowed(userRoles, [roles.MANAGE_4G_RAN_DL_THROUGHPUT]);
    const isKPI4GRANULThroughput = userAllowed(userRoles, [roles.MANAGE_4G_RAN_UL_THROUGHPUT]);
    const isKPI5GRANDLThroughput = userAllowed(userRoles, [roles.MANAGE_5G_RAN_DL_THROUGHPUT]);
    const isKPI5GRANULThroughput = userAllowed(userRoles, [roles.MANAGE_5G_RAN_UL_THROUGHPUT]);
    const isKPI5GCoreDLThroughput = userAllowed(userRoles, [roles.MANAGE_5G_CORE_DL_THROUGHPUT]);
    const isKPI5GCoreULThroughput = userAllowed(userRoles, [roles.MANAGE_5G_CORE_UL_THROUGHPUT]);

    const isEditable =
        title === 'Attach success rate' ||
        title === 'Core network utilization' ||
        ['Accessibility', 'Mobility', 'Retainability'].some((keyword) => title.includes(keyword));

    const config = showSlider
        ? {
              height: 95,
              fontSize: '20',
              yValue: -20
          }
        : {
              height: 155,
              fontSize: '33',
              yValue: -35
          };

    const onSliderChange = ([leftValue, rightValue]) => {
        if (leftValue != level1) setLevel1(leftValue);
        if (rightValue != level3) setLevel3(rightValue);
    };

    const handleOnClose = () => {
        if (level1 != thresholds?.level1) setLevel1(thresholds?.level1);
        if (level3 != thresholds?.level3) setLevel3(thresholds?.level3);
        setShowSlider(false);
    };

    const onSubmitDefault = () => {
        const { level1, level3 } = defaultThresholds;
        setLevel1(level1);
        setLevel3(level3);
    };

    const handleOnSubmit = async () => {
        setModifyLoading(true);
        try {
            const payload = {
                enterpriseId,
                locationId,
                kpiType,
                level1: level1,
                level2: level1,
                level3: level3
            };

            await onSubmit(payload);
            setShowSlider(false);
            setModifyLoading(false);
        } catch ({ message }) {
            console.log('modify threshold API error', message);
            setModifyLoading(false);
        }
    };

    const OverallChart = () => {
        switch (type) {
            case 'Attached devices':
            case 'Registered devices':
                return (
                    <div className="throughput-container">
                        <div className="link">
                            <div className="font-container">
                                <strong className="devices-font">{deviceCount?.enabledCount}</strong>
                            </div>
                            <div>Devices</div>
                        </div>
                    </div>
                );
            case 'Attached success ratio':
                return (
                    <DonutChart
                        data={data}
                        config={{
                            chart: {
                                height: '160px',
                                spacingLeft: 0,
                                spacingRight: 0
                            },
                            colors: COLORS,
                            subtitle: {
                                useHTML: true,
                                text: `<span class="${getMainSubtitleClass(deviceCount?.totalCount)}">${
                                    deviceCount?.totalCount
                                }</span> <br> <span class="subtitle-sub"> Total devices </span>`,
                                verticalAlign: 'middle',
                                align: 'center',
                                x: -74
                            }
                        }}
                    />
                );
            case 'Uplink throughput':
                if (KPIValue == null) {
                    return <div className="no-data-placeholder">No data available</div>;
                }
                return (
                    <div style={{ margin: 20 }}>
                        <div>
                            <img src={uploadThroughPutImg} alt="uplink throughput" />
                        </div>
                        <div style={{ fontSize: 48, margin: 16, fontWeight: 600 }}>{KPIValue}</div>
                        <div>Mbps UL</div>
                    </div>
                );
            case 'Downlink throughput':
                if (KPIValue == null) {
                    return <div className="no-data-placeholder">No data available</div>;
                }
                return (
                    <div style={{ margin: 20 }}>
                        <div>
                            <img src={downlinkThroughPutImg} alt="downlink throughput" />
                        </div>
                        <div style={{ fontSize: 48, margin: 16, fontWeight: 600 }}>{KPIValue}</div>
                        <div>Mbps DL</div>
                    </div>
                );
            case 'Core combined throughput':
            case 'RAN throughput':
            case 'RAN combined throughput':
                if (DlValue == null && UlValue == null) {
                    return <div className="no-data-placeholder">No data available</div>;
                }
                return (
                    <div className="throughput-container">
                        {isKPI4GRANDLThroughput && (
                            <div className="link">
                                <img src={dlImage} alt={dlName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{DlValue}</strong>
                                </div>
                                <div>Mbps DL</div>
                            </div>
                        )}
                        {isKPI4GRANULThroughput && (
                            <div className="link">
                                <img src={ulImage} alt={ulName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{UlValue}</strong>
                                </div>
                                <div>Mbps UL</div>
                            </div>
                        )}
                    </div>
                );
            case 'Core throughput':
                if (DlValue == null && UlValue == null) {
                    return <div className="no-data-placeholder">No data available</div>;
                }
                return (
                    <div className="throughput-container">
                        {isKPI4GCoreDLThroughput && (
                            <div className="link">
                                <img src={dlImage} alt={dlName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{DlValue}</strong>
                                </div>
                                <div>Mbps DL</div>
                            </div>
                        )}
                        {isKPI4GCoreULThroughput && (
                            <div className="link">
                                <img src={ulImage} alt={ulName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{UlValue}</strong>
                                </div>
                                <div>Mbps UL</div>
                            </div>
                        )}
                    </div>
                );
            case '5G Core throughput':
                if (DlValue == null && UlValue == null) {
                    return <div className="no-data-placeholder">No data available</div>;
                }
                return (
                    <div className="throughput-container">
                        {isKPI5GCoreDLThroughput && (
                            <div className="link">
                                <img src={dlImage} alt={dlName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{DlValue}</strong>
                                </div>
                                <div>Mbps DL</div>
                            </div>
                        )}
                        {isKPI5GCoreULThroughput && (
                            <div className="link">
                                <img src={ulImage} alt={ulName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{UlValue}</strong>
                                </div>
                                <div>Mbps UL</div>
                            </div>
                        )}
                    </div>
                );
            case '4G RAN throughput':
                if (DlValue == null && UlValue == null) {
                    return <div className="no-data-placeholder">No data available</div>;
                }
                return (
                    <div className="throughput-container">
                        {isKPI4GRANDLThroughput && (
                            <div className="link">
                                <img src={dlImage} alt={dlName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{DlValue}</strong>
                                </div>
                                <div>Mbps DL</div>
                            </div>
                        )}
                        {isKPI4GRANULThroughput && (
                            <div className="link">
                                <img src={ulImage} alt={ulName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{UlValue}</strong>
                                </div>
                                <div>Mbps UL</div>
                            </div>
                        )}
                    </div>
                );
            case '5G RAN throughput':
                if (DlValue == null && UlValue == null) {
                    return <div className="no-data-placeholder">No data available</div>;
                }
                return (
                    <div className="throughput-container">
                        {isKPI5GRANDLThroughput && (
                            <div className="link">
                                <img src={dlImage} alt={dlName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{DlValue}</strong>
                                </div>
                                <div>Mbps DL</div>
                            </div>
                        )}
                        {isKPI5GRANULThroughput && (
                            <div className="link">
                                <img src={ulImage} alt={ulName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{UlValue}</strong>
                                </div>
                                <div>Mbps UL</div>
                            </div>
                        )}
                    </div>
                );
            case '5G NSA RAN throughput':
                if (DlValue == null && UlValue == null) {
                    return <div className="no-data-placeholder">No data available</div>;
                }
                return (
                    <div className="throughput-container">
                        {isKPI5GRANDLThroughput && (
                            <div className="link">
                                <img src={dlImage} alt={dlName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{DlValue}</strong>
                                </div>
                                <div>Mbps DL</div>
                            </div>
                        )}
                        {isKPI5GRANULThroughput && (
                            <div className="link">
                                <img src={ulImage} alt={ulName} />
                                <div className="font-container">
                                    <strong className="kpi-font">{UlValue}</strong>
                                </div>
                                <div>Mbps UL</div>
                            </div>
                        )}
                    </div>
                );
            default:
                const subTitle = title === 'Core network utilization' ? `${attachedDevicesCount}/${coreCapacity}` : null;
                if (data?.length && (KPIValue === undefined || KPIValue === null || isNaN(Number(KPIValue)))) {
                    return <div className="no-data-placeholder">No data available</div>;
                }
                return data?.length ? (
                    <GaugeChart data={processGaugeChartData(data, KPIValue, COLORS)} subTitle={subTitle} config={config}></GaugeChart>
                ) : null;
        }
    };

    const getTrackColors = (kpiType) => {
        if (['Retainability', 'Accessibility', 'Core network utilization'].some((v) => kpiType.toLowerCase().includes(v.toLowerCase()))) {
            return ['#008331', '#FFBC3D', '#A40000'];
        } else {
            return ['#A40000', '#FFBC3D', '#008331'];
        }
    };

    const Track = ({ props, children }) => (
        <div
            onMouseDown={props.onMouseDown}
            onTouchStart={props.onTouchStart}
            style={{
                ...props.style,
                height: '23px',
                width: '100%'
            }}>
            <div
                ref={props.ref}
                style={{
                    height: '13px',
                    width: '100%',
                    background: getTrackBackground({
                        colors: getTrackColors(type),
                        min: 0,
                        max: 100,
                        values: [level1, level3]
                    }),
                    alignSelf: 'center'
                }}>
                {children}
            </div>
        </div>
    );

    const topValue = (title) => {
        switch (title) {
            case 'VoLTE accessibility':
            case 'VoLTE retainability':
            case 'VoLTE mobility':
                return '-25px';

            default:
                return '-38px';
        }
    };

    return (
        <Card.Content className={showSlider ? 'add-border' : ''}>
            <Card.Header textAlign="left">
                {title}
                <InformationIcon
                    tooltip={tooltip}
                    tooltipStyle={{
                        left: leftValue ?? '0px',
                        top: topValue(title)
                    }}
                />

                {isEditable && (
                    <FontAwesomeIcon
                        icon="fa-solid fa-pencil"
                        size="1x"
                        style={{ float: 'right' }}
                        onClick={() => {
                            setShowSlider(true);
                        }}
                    />
                )}
            </Card.Header>
            <Card.Description textAlign="center" style={{ marginTop: '0px' }}>
                {loading ? (
                    <Placeholder>
                        <Placeholder.Image />
                    </Placeholder>
                ) : (
                    <>
                        <div className={showSlider ? 'minimize-container' : ''}>
                            <OverallChart />
                        </div>
                        {showSlider && (
                            <Grid>
                                <Grid.Row computer="12" mobile="12" className="modify-threshold no-bottom-padding">
                                    <Grid.Column width="8" floated="right">
                                        <Button size="mini" onClick={handleOnSubmit} type="submit" content="Save" disabled={modifyLoading} />
                                        <Button size="mini" content="Default" disabled={modifyLoading} secondary onClick={onSubmitDefault} />
                                        <div className="cross-icon">
                                            <img src={crossIcon} onClick={handleOnClose} />
                                        </div>
                                    </Grid.Column>
                                </Grid.Row>
                                <Grid.Row className="range-container no-bottom-padding">
                                    {level1 && level3 ? (
                                        <Range
                                            values={[level1, level3]}
                                            step={1}
                                            min={0}
                                            max={100}
                                            onChange={onSliderChange}
                                            renderTrack={Track}
                                            renderThumb={({ index, props }) => (
                                                <div
                                                    {...props}
                                                    style={{
                                                        ...props.style,
                                                        height: '20px',
                                                        width: '20px',
                                                        bottom: '3px',
                                                        backgroundColor: '#fff',
                                                        borderRadius: '10px',
                                                        border: 'solid 1px #A7A7A7'
                                                    }}>
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: '15px',
                                                            fontSize: '11px',
                                                            padding: '4px'
                                                        }}>
                                                        {[level1, level3][index] + '%'}
                                                    </div>
                                                </div>
                                            )}
                                        />
                                    ) : null}
                                </Grid.Row>
                            </Grid>
                        )}
                    </>
                )}
            </Card.Description>
        </Card.Content>
    );
};
